import type { ClientType, FileType } from "types/DataTypes";
import { formatCurrency, getVerboseFileStatus } from "utils/format-utils";

import FileNumberPill from "../components/FileNumberPill";
import ServicesPills from "../components/ServicesPills";
import type { InfoPillFields } from "../InfoPillTypes";
import {
  formatClaimantName,
  formatDateOfBirth,
  formatMedicareEligibilityStatus,
  formatSSN,
  getClaimNumbersString,
  getDatesOfInjuryInfo,
  getEmployerName,
  NONE,
  NOT_AVAILABLE,
} from "../InfoPillTypes";

/*
 * Edit File page
 */
export const getFileInfoPillData = (
  isNewFile: boolean,
  currentClient?: ClientType,
  fileData?: FileType,
): InfoPillFields => {
  if (isNewFile) {
    return {
      Claimant: {
        value: undefined,
      },
      Client: {
        value: currentClient?.name,
        action: {
          type: "drawer" as const,
          drawerType: "clientInfo" as const,
        },
      },
    };
  }

  // check if there's a lien service
  const lienService = fileData?.services?.find(
    (service) => service.type === "LIEN",
  );
  const hasLien = Boolean(lienService);

  // check if next_level_of_appeal is available
  const hasAppealInfo = Boolean(fileData?.next_level_of_appeal);

  const baseFields = {
    "File #": {
      value: fileData?.id ? (
        <FileNumberPill fileId={fileData.id} />
      ) : (
        NOT_AVAILABLE
      ),
      disableTooltip: true,
    },
    Claimant: {
      value: formatClaimantName(fileData?.claimants?.[0]),
      action: fileData?.section_111_claimant_url
        ? {
            type: "custom" as const,
            handler: () => {
              window.open(fileData.section_111_claimant_url || undefined);
            },
          }
        : undefined,
    },
    "Claim Numbers": {
      value:
        fileData?.claim_numbers?.map((claim) => claim.number).join(", ") ||
        NONE,
      tooltip: getClaimNumbersString(
        fileData?.claim_numbers,
        fileData?.dates_of_injury,
      ),
      tooltipHtml: true,
    },
    DOB: {
      value: formatDateOfBirth(fileData?.claimants?.[0]?.birth_date),
    },
    SSN: {
      value: formatSSN(fileData?.claimants?.[0]?.ssn),
    },
    Client: {
      value: fileData?.client?.name || NOT_AVAILABLE,
      action: {
        type: "drawer" as const,
        drawerType: "clientInfo" as const,
      },
    },
    [getDatesOfInjuryInfo(fileData?.dates_of_injury).label]: {
      value: getDatesOfInjuryInfo(fileData?.dates_of_injury).value,
    },
    Services: {
      value: fileData?.services?.length ? (
        <ServicesPills services={fileData.services} fileId={fileData.id} />
      ) : (
        NOT_AVAILABLE
      ),
      disableTooltip: true,
    },
    Employer: {
      value: getEmployerName(fileData || {}),
    },
    "Insurance Type": {
      value:
        fileData?.insurance_types?.map((i) => i.label).join(", ") ||
        NOT_AVAILABLE,
    },
    "Medicare Eligibility Status": {
      value: formatMedicareEligibilityStatus(
        fileData?.claimants?.[0]?.is_medicare_eligible,
      ),
    },
    "File Status": {
      value: getVerboseFileStatus(fileData?.status || ""),
      classNames: "status unprocessed",
    },
    "File Adjuster": {
      value: fileData?.adjuster?.name || NOT_AVAILABLE,
    },
  };

  // if there's no lien, just return base fields without lien info
  if (!hasLien) return baseFields;

  // add Total Savings only for files with lien service
  const fieldsWithLien = {
    ...baseFields,
    "Total Savings": {
      value: formatCurrency(fileData?.total_savings || 0),
    },
  };

  // if there's no appeal info, just return fields with lien info
  if (!hasAppealInfo) return fieldsWithLien;

  // add lien-specific fields from next_level_of_appeal
  const appealInfo = fileData?.next_level_of_appeal;
  return {
    ...fieldsWithLien,
    "Appeal Level": {
      value: appealInfo?.appeal_level || NOT_AVAILABLE,
    },
    "Case #": {
      value: appealInfo?.case_number || NOT_AVAILABLE,
    },
    "Total Amount": {
      value: formatCurrency(appealInfo?.amount || 0),
    },
    "Amount Disputed": {
      value: formatCurrency(appealInfo?.amount_disputed || 0),
    },
    "Amount Due": {
      value: formatCurrency(fileData?.next_total_amount_to_pay || 0),
    },
    "Payment Due Date": {
      value: appealInfo?.payment_due_date || NOT_AVAILABLE,
    },
    "Appeal Due Date": {
      value: appealInfo?.appeal_due_date || NOT_AVAILABLE,
    },
  };
};
